import React, { useState, useMemo } from 'react';
import { CARD_MODE, GRID_MODE, OPERATORS, TYPES } from '../../../../common/list-utils';
import { DefaultPalette } from '@fluentui/react/lib/Styling';
import { ListColumnFilter } from '../../../Common/list-entities/ListColumnFilter';
import TimeTaxSupportSystemClient from '../../../../system';
import { IComboBoxOption, ICommandBarItemProps } from '@fluentui/react';
import { buttonStylesForProjects, defaultCommandBarButtonStyles } from '../../../../styles/button-styles';
import { routes } from '../../../../common/constants';
import { useNavigate } from 'react-router-dom';
import ViewWrapperWithCommandBar from '../../../Layout/ViewWrapperWithCommandBar';
import { useTranslation } from 'react-i18next';
import MessageCourier from '../../../../lib/MessageCourier';
import useSystemCall from '../../../../common/custom-hooks/useSystemCall';
import Loading from '../../../Spinners/Loading';
import useAgrupadorCorporacionLiquidadoraList from '../../../../shared/hooks/useAgrupadorCorporacionLiquidadoraList';
import { DateCustom } from '../../../../lib/DateCustom';
import { ReportHoursSettlementGridList } from './ReportHoursSettlementGridList';
import { ReportHoursSettlementEstado } from '../../../../system/report_hours_settlement/ReportHoursSettlementSystem';

interface IDirectSettlementsListAje {
  system: TimeTaxSupportSystemClient;
  changeBlockUI: (state: boolean) => void;
  courier: MessageCourier;
}

export const ReportHoursSettlement = (props: IDirectSettlementsListAje) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(GRID_MODE);
  const agrupadoresCorporacionLiquidadora = useAgrupadorCorporacionLiquidadoraList(props.system);

  const tipos_de_liquidacion: IComboBoxOption[] = React.useMemo(() => {
    return agrupadoresCorporacionLiquidadora.data.map((a) => ({ key: a.agrcorliq_id, text: a.agrcorliq_nombre }));
  }, [agrupadoresCorporacionLiquidadora]);

  const navigate = useNavigate();

  const baseUrl = React.useMemo(() => {
    return props.system.getReportHoursSettlementSystem().getBaseUrlForDatagrid();
  }, [props]);

  const onItemClick = (item: any) => {
    props.courier.messageError('');

    // Si el estado esta [REGISTRADO, PROCESANDO, REPROCESAR] no se puede ingresar a la liquidacion porque esta en proceso de ejecucion
    // if ([ReportHoursSettlementEstado.REGISTRADO, ReportHoursSettlementEstado.PROCESANDO, ReportHoursSettlementEstado.PENDIENTE].includes(item.sexpasinc_estado)) {
    //   props.courier.messageError(t('La liquidación seleccionada está en proceso de ejecución.'));
    //   return;
    // }

    navigate(`${routes.detail_report_hours_settlement}/${item.sexpasinc_id}`);
  };

  const commonContext = useSystemCall(props.system, (system) => system.getActivitySystem().getCommonContext());

  const collaboratorList = React.useMemo(() => {
    let ret: IComboBoxOption[] = [];
    if (commonContext) {
      ret = commonContext.colaboradores.map((col) => {
        return {
          key: col.getId(),
          text: col.fullName(),
        };
      });
    }
    return ret;
  }, [commonContext]);

  const estadosOptions: IComboBoxOption[] = React.useMemo(() => {
    return [
      { key: ReportHoursSettlementEstado.REGISTRADO, text: t('Registrado') },
      { key: ReportHoursSettlementEstado.PENDIENTE, text: t('Pendiente') },
      { key: ReportHoursSettlementEstado.PROCESANDO, text: t('Procesando') },
      { key: ReportHoursSettlementEstado.ACEPTADO, text: t('Aceptado') },
      { key: ReportHoursSettlementEstado.ELIMINADO, text: t('Eliminado') },
      { key: ReportHoursSettlementEstado.PREGENERADO, text: t('Pregenerado') },
    ];
  }, []);

  const quickFilters: ListColumnFilter[] = useMemo(
    () => [
      {
        quick: true,
        name: 'sexpasinc_id',
        type: TYPES.INPUT,
        placeholder: t('ID'),
        operator: OPERATORS.EQUAL,
      },
      {
        quick: true,
        name: 'sexpasinc_agrcorliq_tipo',
        type: TYPES.COMBOBOXTEXT,
        placeholder: t('Tipo de liquidacion'),
        operator: OPERATORS.CONTAINS,
        contextForCombobox: tipos_de_liquidacion,
      },
      {
        quick: true,
        name: 'sexpasinc_periodo',
        type: TYPES.TYPE_DATE_YEAR_MONTH,
        placeholder: t('Periodo'),
        operator: OPERATORS.YEAR_MONTH_EQUAL_FORMAT_YYYYMM,
        calendarProps: { isDayPickerVisible: false },
        isMonthSelector: true,
        formatDate: DateCustom.formatDateMMYYYY,
      },
      {
        quick: true,
        name: 'sexpasinc_estado',
        type: TYPES.COMBOBOXTEXT,
        placeholder: t('Estado'),
        operator: OPERATORS.EQUAL,
        contextForCombobox: estadosOptions,
      },
      {
        quick: true,
        name: 'sexpasinc_activo',
        type: TYPES.CHECKBOX,
        labelForCheckbox: t('Activos'),
        checkBoxValue: '1',
        operator: OPERATORS.EQUAL,
      },
      {
        quick: false,
        name: 'sexpasinc_fecha_ejecucion',
        type: TYPES.DATE,
        placeholder: t('Fecha de carga'),
        operator: OPERATORS.AFTER,
      },
      {
        quick: false,
        name: 'colaborador',
        type: TYPES.COMBOBOXTEXT,
        placeholder: t('Generada por'),
        operator: OPERATORS.EQUAL,
        contextForCombobox: collaboratorList || [],
      },
    ],
    [tipos_de_liquidacion, collaboratorList, estadosOptions],
  );

  const itemsMenuCalendar = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'add',
        text: t('Nuevo'),
        iconProps: { iconName: 'Add', color: DefaultPalette.themeLighterAlt },
        buttonStyles: buttonStylesForProjects,
        onClick: () => navigate(`${routes.assist_report_hours_settlement}`),
      },
    ],
    [navigate],
  );

  if (agrupadoresCorporacionLiquidadora.loading) {
    return <Loading title="Cargando" description="Por favor espere estamos procesando la información necesaria para completar su solicitud." />;
  }

  return (
    <ViewWrapperWithCommandBar
      withBorderBottom
      title={''}
      items={itemsMenuCalendar}
      overflowButtonStyles={defaultCommandBarButtonStyles}
      variant="standard"
    >
      <>
        <ReportHoursSettlementGridList
          changeBlockUI={props.changeBlockUI}
          baseUrl={baseUrl}
          quickFilters={quickFilters}
          onClickCardMode={() => setMode(CARD_MODE)}
          onClickGridMode={() => setMode(GRID_MODE)}
          mode={mode}
          keyList={'liquidaciones_directas'}
          onItemClick={onItemClick}
          courier={props.courier}
        ></ReportHoursSettlementGridList>
      </>
    </ViewWrapperWithCommandBar>
  );
};
